import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

type MenuItem = {
  title: string;
  submenu?: { title: string; href: string }[];
  href?: string;
};

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems: MenuItem[] = [
    {
      title: '会社概要',
      submenu: [
        { title: '会社情報', href: '/company' },
        { title: '経営指針', href: '/policy' },
        { title: '情報セキュリティ基本方針', href: '/security' },
        { title: '健康企業宣言', href: '/health' },
        { title: '社長インタビュー', href: '/interview' },
      ],
    },
    {
      title: '事業案内',
      submenu: [
        { title: '事業内容', href: '/service' },
        { title: '開発実績', href: '/record' },
      ],
    },
    {
      title: 'プロダクト',
      href: '/products',
    },
    {
      title: '採用情報',
      href: '/recruit',
    },
    {
      title: 'お問い合わせ',
      href: '/contact',
    },
  ];

  const toggleSubmenu = (title: string) => {
    setOpenSubmenu(openSubmenu === title ? null : title);
  };

  const handleLinkClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('/')) {
      navigate(href);
    } else if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/' + href);
      }
    }
  };

  const allMobileItems: MenuItem[] = [
    ...menuItems,
    { title: 'アクセス', href: '/access' },
  ];

  const renderMenuItem = (item: MenuItem) => (
    <li key={item.title} className="relative group">
      {item.submenu ? (
        <>
          <button
            className="px-4 py-2 text-[13px] font-medium transition-all duration-300 rounded-md whitespace-nowrap cursor-pointer flex items-center gap-1 text-[#89B56B] hover:text-[#3F6844] hover:bg-[#89B56B]/8"
          >
            {item.title}
            <i className="ri-arrow-down-s-line text-xs opacity-60"></i>
          </button>
          <ul className="absolute top-full left-0 mt-1 bg-white shadow-lg shadow-black/5 rounded-xl py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100 z-50">
            {item.submenu.map((subItem) => (
              <li key={subItem.title}>
                <a
                  href={subItem.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick(subItem.href);
                  }}
                  className="block px-5 py-2.5 text-[13px] text-[#89B56B]/70 hover:text-[#3F6844] hover:bg-[#89B56B]/5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {subItem.title}
                </a>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <a
          href={item.href ?? '#'}
          onClick={(e) => {
            e.preventDefault();
            handleLinkClick(item.href ?? '#');
          }}
          className="px-4 py-2 text-[13px] font-medium transition-all duration-300 rounded-md whitespace-nowrap cursor-pointer text-[#89B56B] hover:text-[#3F6844] hover:bg-[#89B56B]/8"
        >
          {item.title}
        </a>
      )}
    </li>
  );

  return (
    <header className="relative z-50 bg-white shadow-sm">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-[88px]">
          {/* Logo (left) */}
          <h1 className="hidden lg:flex flex-shrink-0">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              className="flex items-center cursor-pointer"
            >
              <img
                src="/image/logo.png"
                alt="株式会社MSK"
                className="h-20 w-auto"
              />
            </a>
          </h1>

          {/* Desktop navigation (center) */}
          <nav className="hidden lg:flex flex-1 items-center justify-center">
            <ul className="flex items-center justify-center gap-1">
              {menuItems.map(renderMenuItem)}
            </ul>
          </nav>

          {/* Mobile logo */}
          <h1 className="flex-shrink-0 lg:hidden">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              className="flex items-center cursor-pointer"
            >
              <img
                src="/image/logo.png"
                alt="株式会社MSK"
                className="h-20 w-auto"
              />
            </a>
          </h1>

          {/* Access icon (desktop, right) */}
          <a
            href="/access"
            onClick={(e) => {
              e.preventDefault();
              handleLinkClick('/access');
            }}
            className="hidden lg:flex w-10 h-10 items-center justify-center rounded-full text-[#89B56B] hover:text-[#3F6844] hover:bg-[#89B56B]/10 transition-all duration-300 cursor-pointer flex-shrink-0"
            title="アクセス"
            aria-label="アクセス"
          >
            <i className="ri-map-pin-line text-xl"></i>
          </a>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-lg transition-colors cursor-pointer text-[#89B56B]"
            aria-label="メニュー"
          >
            <span
              className={`w-5 h-0.5 bg-current transition-transform duration-300 ${
                isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
              }`}
            ></span>
            <span
              className={`w-5 h-0.5 bg-current transition-opacity duration-300 ${
                isMobileMenuOpen ? 'opacity-0' : ''
              }`}
            ></span>
            <span
              className={`w-5 h-0.5 bg-current transition-transform duration-300 ${
                isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
              }`}
            ></span>
          </button>
        </div>
      </div>

      {/* Mobile navigation panel */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 max-h-[calc(100vh-88px)] overflow-y-auto">
          <nav className="px-6 py-4">
            <ul className="space-y-1">
              {allMobileItems.map((item) => (
                <li key={item.title}>
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => toggleSubmenu(item.title)}
                        className="w-full text-left py-3 px-3 text-sm font-medium text-[#89B56B] hover:text-[#3F6844] hover:bg-[#89B56B]/5 rounded-lg transition-colors flex items-center justify-between whitespace-nowrap cursor-pointer"
                      >
                        {item.title}
                        <i
                          className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                            openSubmenu === item.title ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </button>
                      {openSubmenu === item.title && (
                        <ul className="pl-4 mt-1 space-y-1">
                          {item.submenu.map((subItem) => (
                            <li key={subItem.title}>
                              <a
                                href={subItem.href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleLinkClick(subItem.href);
                                }}
                                className="block py-2.5 px-3 text-sm text-[#89B56B]/70 hover:text-[#3F6844] hover:bg-[#89B56B]/5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                              >
                                {subItem.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <a
                      href={item.href ?? '#'}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClick(item.href ?? '#');
                      }}
                      className="block py-3 px-3 text-sm font-medium text-[#89B56B] hover:text-[#3F6844] hover:bg-[#89B56B]/5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {item.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
